import { loadVeneraPlugin } from '../_venera/load.ts';
import { proxyFetch as defaultProxyFetch } from '../_venera/network.ts';
import { decodeBase64, hmacString } from '../_venera/convert.ts';
import type { VeneraComicSource, VeneraLoadOptions } from '../_venera/types.ts';
import type {
  ComicCard,
  ComicDetails,
  ComicSource,
  ListOpts,
  Page,
  PageRef,
  PopularOpts,
  RecommendationOpts,
  SearchFiltersSchema,
  SearchQuery,
  SourceAuth,
  SourceComment,
  SourceCommentOpts,
  SourceCommentPage,
  Tag,
} from '../types.ts';
import { NetworkError, ParseError } from '../../lib/errors.ts';
import { absoluteUrl, parseDocument, textOf } from '../../lib/html.ts';
import copyMangaPlugin from './_plugin/copy_manga.js?raw';

const COPY_MANGA_SOURCE_ID = 'copy_manga' as const;

const COPY_MANGA_DEFAULTS = {
  base_url: 'api.copy2000.online',
  region: '0',
  image_quality: '1500',
  search_api: 'baseAPI',
  favorites_ordering: '-datetime_updated',
};

const COPY_MANGA_THROTTLE_STATUS = 210;
const COPY_MANGA_THROTTLE_MAX_ATTEMPTS = 5;
const COPY_MANGA_BRIDGE_SAFE_WAIT_MS = 1_000;
const COPY_MANGA_REWRITTEN_THROTTLE_STATUS = 429;
const COPY_MANGA_WEB_DETAIL_MAX_ATTEMPTS = 3;
const COPY_MANGA_WEB_DETAIL_RETRY_WAIT_MS = 250;
const COPY_MANGA_WEB_CHAPTERS_MAX_ATTEMPTS = 3;
const COPY_MANGA_WEB_CHAPTERS_RETRY_WAIT_MS = 500;
const COPY_MANGA_WEB_READER_MAX_ATTEMPTS = 5;
const COPY_MANGA_WEB_READER_RETRY_WAIT_MS = 500;
const COPY_MANGA_WEB_BASES = ['https://www.mangacopy.com', 'https://www.copy20.com'] as const;
const COPY_MANGA_WEB_API_BASE = 'https://api.2026copy.com';
const COPY_MANGA_APP_API_BASES = ['https://api.copy2000.online', 'https://api.2026copy.com'] as const;
const COPY_MANGA_WEB_IMAGE_COOKIES = 'webp=1';
const COPY_MANGA_APP_VERSION = '3.0.6';
const COPY_MANGA_APP_HMAC_SECRET = 'M2FmMDg1OTAzMTEwMzJlZmUwNjYwNTUwYTA1NjNhNTM=';
const COPY_MANGA_APP_UMSTRING = 'b4c89ca4104ea9a97750314d791520ac';

const COPY_MANGA_CAPABILITIES = {
  login: true,
  favorites: true,
  multiFavCategories: false,
  search: true,
  advancedSearch: true,
  recommendations: true,
  comments: true,
  loginNotes:
    'Copy Manga uses username/password form login. Credentials are sent to the Copy Manga API through Bunko’s proxy; Bunko stores only the resulting token and plugin device identity.',
};

const COPY_MANGA_THEME_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'aiqing', label: '愛情' },
  { value: 'huanlexiang', label: '歡樂向' },
  { value: 'maoxian', label: '冒險' },
  { value: 'qihuan', label: '奇幻' },
  { value: 'baihe', label: '百合' },
  { value: 'xiaoyuan', label: '校园' },
  { value: 'kehuan', label: '科幻' },
  { value: 'dongfang', label: '東方' },
  { value: 'danmei', label: '耽美' },
  { value: 'shenghuo', label: '生活' },
  { value: 'gedou', label: '格鬥' },
  { value: 'qingxiaoshuo', label: '轻小说' },
  { value: 'xuanyi', label: '悬疑' },
  { value: 'qita', label: '其他' },
  { value: 'shengui', label: '神鬼' },
  { value: 'zhichang', label: '职场' },
  { value: 'teenslove', label: 'TL' },
  { value: 'mengxi', label: '萌系' },
  { value: 'zhiyu', label: '治愈' },
  { value: 'changtiao', label: '長條' },
  { value: 'sige', label: '四格' },
  { value: 'jiecao', label: '节操' },
  { value: 'jianniang', label: '舰娘' },
  { value: 'jingji', label: '竞技' },
  { value: 'gaoxiao', label: '搞笑' },
  { value: 'weiniang', label: '伪娘' },
  { value: 'rexue', label: '热血' },
  { value: 'lizhi', label: '励志' },
  { value: 'xingzhuanhuan', label: '性转换' },
  { value: 'COLOR', label: '彩色' },
  { value: 'hougong', label: '後宮' },
  { value: 'meishi', label: '美食' },
  { value: 'zhentan', label: '侦探' },
  { value: 'aa', label: 'AA' },
  { value: 'yinyuewudao', label: '音乐舞蹈' },
  { value: 'mohuan', label: '魔幻' },
  { value: 'zhanzheng', label: '战争' },
  { value: 'lishi', label: '历史' },
  { value: 'yishijie', label: '异世界' },
  { value: 'jingsong', label: '惊悚' },
  { value: 'jizhan', label: '机战' },
  { value: 'dushi', label: '都市' },
  { value: 'chuanyue', label: '穿越' },
  { value: 'kongbu', label: '恐怖' },
  { value: 'comiket100', label: 'C100' },
  { value: 'chongsheng', label: '重生' },
  { value: 'comiket99', label: 'C99' },
  { value: 'comiket101', label: 'C101' },
  { value: 'comiket97', label: 'C97' },
  { value: 'comiket96', label: 'C96' },
  { value: 'shengcun', label: '生存' },
  { value: 'zhaixi', label: '宅系' },
  { value: 'wuxia', label: '武侠' },
  { value: 'C98', label: 'C98' },
  { value: 'comiket95', label: 'C95' },
  { value: 'fate', label: 'FATE' },
  { value: 'zhuansheng', label: '转生' },
  { value: 'Uncensored', label: '無修正' },
  { value: 'xianxia', label: '仙侠' },
  { value: 'loveLive', label: 'LoveLive' },
];

const COPY_MANGA_RECOMMENDATION_OPTIONS = [
  { id: 'home', label: 'Home recommendations', exploreKey: '拷贝漫画', default: true },
  {
    id: 'ranking',
    label: 'Ranking',
    controls: [
      {
        id: 'audience',
        type: 'chips',
        label: 'Audience',
        options: [
          { value: 'male', label: 'Male' },
          { value: 'female', label: 'Female' },
        ],
      },
      {
        id: 'window',
        type: 'chips',
        label: 'Window',
        defaultValue: 'week',
        options: [
          { value: 'day', label: 'Rising' },
          { value: 'week', label: '7 days' },
          { value: 'month', label: '30 days' },
          { value: 'total', label: 'All time' },
        ],
      },
    ],
  },
  {
    id: 'themes',
    label: 'Themes',
    controls: [
      {
        id: 'theme',
        type: 'searchable-select',
        label: 'Theme',
        defaultValue: 'maoxian',
        options: COPY_MANGA_THEME_OPTIONS,
      },
      {
        id: 'ordering',
        type: 'chips',
        label: 'Order',
        options: [
          { value: '-popular', label: 'Most popular' },
          { value: '-datetime_updated', label: 'Newest' },
          { value: 'popular', label: 'Least popular' },
          { value: 'datetime_updated', label: 'Oldest' },
        ],
      },
      {
        id: 'top',
        type: 'chips',
        label: 'Filter',
        options: [
          { value: '', label: 'All' },
          { value: 'japan', label: 'Japan' },
          { value: 'korea', label: 'Korea' },
          { value: 'west', label: 'West' },
          { value: 'finish', label: 'Finished' },
        ],
      },
    ],
  },
] satisfies Array<
  NonNullable<ComicSource['recommendationOptions']>[number] & { exploreKey?: string }
>;

const COPY_MANGA_EXPLORE_RECOMMENDATION_OPTIONS = [
  { id: 'home', label: 'Home recommendations', exploreKey: '拷贝漫画', default: true },
];

const COPY_MANGA_SEARCH_FILTERS: SearchFiltersSchema = {
  fields: [
    {
      id: 'q_type',
      type: 'select',
      label: 'Search option',
      options: [
        { value: '', label: 'All' },
        { value: 'name', label: 'Title' },
        { value: 'author', label: 'Author' },
        { value: 'local', label: 'Scanlation group' },
      ],
    },
  ],
};

type CopyMangaTestOptions = Pick<VeneraLoadOptions, 'proxyFetch' | 'saveAuth' | 'settings'>;

let sharedSourcePromise: Promise<VeneraComicSource> | undefined;

interface CopyMangaWebProfile {
  detailUrl: string;
  html: string;
  key: string;
  document: Document;
  readGroups: NonNullable<ComicDetails['readGroups']>;
}

const COPY_MANGA_WEB_PROFILE_CACHE_MS = 30_000;
const copyMangaWebProfileCaches = new WeakMap<
  NonNullable<VeneraLoadOptions['proxyFetch']>,
  Map<string, { expiresAt: number; promise: Promise<CopyMangaWebProfile> }>
>();

async function createCopyMangaSource(
  overrides: CopyMangaTestOptions = {},
): Promise<VeneraComicSource> {
  if (
    overrides.proxyFetch === undefined &&
    overrides.saveAuth === undefined &&
    overrides.settings === undefined
  ) {
    sharedSourcePromise ??= createCopyMangaSourceInstance();
    return sharedSourcePromise;
  }
  return createCopyMangaSourceInstance(overrides);
}

async function createCopyMangaSourceInstance(
  overrides: CopyMangaTestOptions = {},
): Promise<VeneraComicSource> {
  const proxyFetch = withCopyMangaThrottleRetry(overrides.proxyFetch ?? defaultProxyFetch);
  const plugin = await loadVeneraPlugin({
    sourceId: COPY_MANGA_SOURCE_ID,
    jsSource: `${copyMangaPlugin}\nnew CopyManga();`,
    defaults: COPY_MANGA_DEFAULTS,
    capabilities: COPY_MANGA_CAPABILITIES,
    searchFilters: COPY_MANGA_SEARCH_FILTERS,
    recommendationExploreKeys: [...COPY_MANGA_EXPLORE_RECOMMENDATION_OPTIONS],
    ...overrides,
    proxyFetch,
  });
  return plugin;
}

function buildCopyMangaFacade(
  loadDelegate: () => Promise<VeneraComicSource>,
  webFetch: NonNullable<VeneraLoadOptions['proxyFetch']>,
): ComicSource {
  return {
    id: COPY_MANGA_SOURCE_ID,
    name: '拷贝漫画',
    description: 'Venera-backed Copy Manga source with form login and direct image URLs.',
    capabilities: COPY_MANGA_CAPABILITIES,
    searchFilters: COPY_MANGA_SEARCH_FILTERS,
    recommendationOptions: COPY_MANGA_RECOMMENDATION_OPTIONS.map(
      ({ exploreKey: _exploreKey, ...option }) => option,
    ),
    async search(q: SearchQuery, auth?: SourceAuth) {
      return (await loadDelegate()).search(q, auth);
    },
    async getPopular(opts?: PopularOpts, auth?: SourceAuth) {
      return (await loadDelegate()).getPopular(opts, auth);
    },
    async getRecommendations(opts?: RecommendationOpts, auth?: SourceAuth) {
      const plugin = await loadDelegate();
      if (opts?.optionId === 'ranking') {
        return loadCopyMangaCategoryRecommendations(plugin, opts, auth, {
          category: '排行',
          param: 'ranking',
          options: [stringExtra(opts, 'audience') ?? 'male', stringExtra(opts, 'window') ?? 'week'],
        });
      }
      if (opts?.optionId === 'themes') {
        return loadCopyMangaCategoryRecommendations(plugin, opts, auth, {
          category: undefined,
          param: stringExtra(opts, 'theme') ?? 'maoxian',
          options: [stringExtra(opts, 'top') ?? '', stringExtra(opts, 'ordering') ?? '-popular'],
        });
      }
      return (
        plugin.getRecommendations?.(opts, auth) ?? {
          items: [],
          page: opts?.page ?? 1,
          hasMore: false,
        }
      );
    },
    async getComic(id: string, _auth?: SourceAuth) {
      return loadCopyMangaWebComic(id, webFetch);
    },
    async getPages(id: string, _auth?: SourceAuth, opts?: { readUnitId?: string }) {
      const readUnitId = opts?.readUnitId ?? (await firstCopyMangaWebReadUnitId(id, webFetch));
      if (!readUnitId) {
        throw new ParseError('Copy Manga web detail did not include any readable chapters', {
          source: COPY_MANGA_SOURCE_ID,
        });
      }
      return loadCopyMangaWebPages(id, readUnitId, webFetch);
    },
    async getComments(
      id: string,
      auth?: SourceAuth,
      opts?: SourceCommentOpts,
    ): Promise<SourceCommentPage> {
      void auth;
      return loadCopyMangaWebComments(id, webFetch, opts);
    },
    async resolveImageUrl(ref: PageRef, auth?: SourceAuth) {
      return (await loadDelegate()).resolveImageUrl(ref, auth);
    },
    async validateAuth(auth: SourceAuth) {
      return (await loadDelegate()).validateAuth?.(auth) ?? { ok: false };
    },
    async loginWithForm(request: { username: string; password: string }) {
      return (await loadDelegate()).loginWithForm?.(request) ?? { ok: false };
    },
    async listFavorites(auth: SourceAuth, opts: ListOpts) {
      return (
        (await loadDelegate()).listFavorites?.(auth, opts) ?? { items: [], page: 1, hasMore: false }
      );
    },
    async addFavorite(id: string, auth: SourceAuth, category?: number) {
      return (await loadDelegate()).addFavorite?.(id, auth, category);
    },
    async removeFavorite(id: string, auth: SourceAuth) {
      return (await loadDelegate()).removeFavorite?.(id, auth);
    },
  };
}

async function createCopyMangaFacadeForTest(
  overrides: CopyMangaTestOptions = {},
): Promise<ComicSource> {
  let delegate: Promise<VeneraComicSource> | undefined;
  const webFetch = overrides.proxyFetch ?? defaultProxyFetch;
  return buildCopyMangaFacade(() => {
    delegate ??= createCopyMangaSource(overrides);
    return delegate;
  }, webFetch);
}

const source = {
  ...buildCopyMangaFacade(createCopyMangaSource, defaultProxyFetch),
  createForTest: createCopyMangaFacadeForTest,
} satisfies ComicSource & { createForTest: typeof createCopyMangaFacadeForTest };

async function loadCopyMangaCategoryRecommendations(
  plugin: VeneraComicSource,
  opts: RecommendationOpts | undefined,
  auth: SourceAuth | undefined,
  request: { category: string | undefined; param: string; options: string[] },
): Promise<Page<ComicCard>> {
  const page = Math.max(1, opts?.page ?? 1);
  const result = await plugin.testInternals.callPluginMethod(
    'categoryComics.load',
    [request.category, request.param, request.options, page],
    auth,
  );
  return mapCopyMangaPageResult(result, page);
}

function mapCopyMangaPageResult(value: unknown, page: number): Page<ComicCard> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { items: [], page, hasMore: false };
  }
  const record = value as { comics?: unknown; maxPage?: unknown };
  const comics = Array.isArray(record.comics) ? record.comics : [];
  const maxPage = typeof record.maxPage === 'number' ? record.maxPage : undefined;
  return {
    items: comics.flatMap((comic): ComicCard[] => {
      if (!comic || typeof comic !== 'object' || Array.isArray(comic)) return [];
      const row = comic as Record<string, unknown>;
      const id = stringField(row.id);
      const title = stringField(row.title);
      const coverUrl = stringField(row.cover);
      if (!id || !title || !coverUrl) return [];
      const card: ComicCard = { id, title, coverUrl, source: COPY_MANGA_SOURCE_ID };
      if (Array.isArray(row.tags)) {
        card.tags = row.tags
          .filter((tag): tag is string => typeof tag === 'string')
          .map((tag) => ({
            id: tag,
            name: tag,
          }));
      }
      return [card];
    }),
    page,
    hasMore: maxPage !== undefined ? page < maxPage : comics.length > 0,
    totalPages: maxPage,
  };
}

function stringExtra(opts: RecommendationOpts | undefined, key: string): string | undefined {
  return stringField(opts?.extra?.[key]);
}

function stringField(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number') return String(value);
  return undefined;
}

function numberField(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value)))
    return Number(value);
  return undefined;
}

function timestampField(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function looksLikeUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

async function loadCopyMangaWebComic(
  id: string,
  proxyFetch: NonNullable<VeneraLoadOptions['proxyFetch']>,
): Promise<ComicDetails> {
  return tryCopyMangaWebBases(async (baseUrl) => {
    const { detailUrl, html, document, readGroups } = await loadCopyMangaWebProfile(
      id,
      proxyFetch,
      baseUrl,
    );
    const title = textOf(document.querySelector('.comicParticulars-title h6'));
    const rawCoverUrl =
      document.querySelector('.comicParticulars-left-img img')?.getAttribute('data-src') ??
      document.querySelector('.comicParticulars-left-img img')?.getAttribute('src') ??
      '';
    if (!title || !rawCoverUrl) {
      throw new ParseError('Copy Manga web detail is missing title or cover', {
        source: COPY_MANGA_SOURCE_ID,
        snippet: html.slice(0, 500),
      });
    }

    const authors = Array.from(
      document.querySelectorAll('.comicParticulars-right-txt a[href^="/author/"]'),
    )
      .map((element) => textOf(element))
      .filter(Boolean);
    const updateTime = labeledDetailText(document, '最後更新');
    const status = labeledDetailText(document, '狀態');
    const detailTags = Array.from(document.querySelectorAll('.comicParticulars-left-theme-all a'))
      .map((element) => textOf(element).replace(/^#/, ''))
      .filter(Boolean);
    const tags: Tag[] = [
      ...authors.map((name) => ({ id: `作者:${name}`, name, namespace: '作者' })),
      ...(updateTime ? [{ id: `更新:${updateTime}`, name: updateTime, namespace: '更新' }] : []),
      ...detailTags.map((name) => ({ id: `标签:${name}`, name, namespace: '标签' })),
      ...(status ? [{ id: `状态:${status}`, name: status, namespace: '状态' }] : []),
    ];

    const details: ComicDetails = {
      id,
      source: COPY_MANGA_SOURCE_ID,
      title,
      coverUrl: absoluteUrl(rawCoverUrl, detailUrl),
      pageCount: 0,
      tags,
      sourceUrl: detailUrl,
    };

    const altTitle = labeledDetailText(document, '別名');
    if (altTitle) details.altTitle = altTitle;
    const description = textOf(document.querySelector('.comicParticulars-synopsis .intro'));
    if (description) details.description = description;
    if (authors[0]) details.uploader = authors[0];
    if (readGroups.length > 0) details.readGroups = readGroups;
    return details;
  }, 'detail');
}

async function loadCopyMangaWebProfile(
  id: string,
  proxyFetch: NonNullable<VeneraLoadOptions['proxyFetch']>,
  baseUrl: string,
): Promise<CopyMangaWebProfile> {
  const cache = copyMangaWebProfileCacheFor(proxyFetch);
  const cacheKey = `${baseUrl}\n${id}`;
  const cached = cache.get(cacheKey);
  const now = Date.now();
  if (cached && cached.expiresAt > now) return cached.promise;
  if (cached) cache.delete(cacheKey);

  const promise = loadCopyMangaWebProfileUncached(id, proxyFetch, baseUrl).catch((err) => {
    cache.delete(cacheKey);
    throw err;
  });
  cache.set(cacheKey, {
    expiresAt: now + COPY_MANGA_WEB_PROFILE_CACHE_MS,
    promise,
  });
  return promise;
}

function copyMangaWebProfileCacheFor(
  proxyFetch: NonNullable<VeneraLoadOptions['proxyFetch']>,
): Map<string, { expiresAt: number; promise: Promise<CopyMangaWebProfile> }> {
  let cache = copyMangaWebProfileCaches.get(proxyFetch);
  if (!cache) {
    cache = new Map();
    copyMangaWebProfileCaches.set(proxyFetch, cache);
  }
  return cache;
}

async function loadCopyMangaWebProfileUncached(
  id: string,
  proxyFetch: NonNullable<VeneraLoadOptions['proxyFetch']>,
  baseUrl: string,
): Promise<CopyMangaWebProfile> {
  const detailUrl = `${baseUrl}/comic/${encodeURIComponent(id)}`;
  const { html, key } = await fetchCopyMangaDetailHtml(detailUrl, proxyFetch);
  const document = parseDocument(html);
  try {
    const readGroups = mapCopyMangaWebReadGroups(
      await loadCopyMangaWebChaptersWithKey(id, proxyFetch, baseUrl, key),
    );
    return { detailUrl, html, key, document, readGroups };
  } catch (err) {
    const fallback = copyMangaWebDetailReadGroups(document, id);
    if (fallback.length > 0) {
      return { detailUrl, html, key, document, readGroups: fallback };
    }
    throw err;
  }
}

async function loadCopyMangaWebComments(
  id: string,
  proxyFetch: NonNullable<VeneraLoadOptions['proxyFetch']>,
  opts?: SourceCommentOpts,
): Promise<SourceCommentPage> {
  const page = Math.max(1, opts?.page ?? 1);
  const pageSize = Math.max(1, opts?.pageSize ?? 20);
  const comicUuid = await loadCopyMangaWebComicUuid(id, proxyFetch);
  const url = new URL('/api/v3/comments', COPY_MANGA_WEB_API_BASE);
  url.searchParams.set('comic_id', comicUuid);
  url.searchParams.set('limit', String(pageSize));
  url.searchParams.set('offset', String((page - 1) * pageSize));
  if (opts?.parentId) {
    url.searchParams.set('reply_id', opts.parentId);
    url.searchParams.set('_update', 'true');
  }

  const response = await proxyFetch(url.toString(), {
    headers: { Accept: 'application/json' },
    cookies: COPY_MANGA_WEB_IMAGE_COOKIES,
  });
  if (!response.ok) {
    throw new NetworkError(`Copy Manga comments request failed: ${response.status}`, {
      source: COPY_MANGA_SOURCE_ID,
      status: response.status,
    });
  }

  const text = await response.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text) as unknown;
  } catch (err) {
    throw new ParseError('Copy Manga comments response is not valid JSON', {
      source: COPY_MANGA_SOURCE_ID,
      snippet: text.slice(0, 500),
      cause: err,
    });
  }
  const payload = asRecord(asRecord(parsed, 'comments response').results, 'comments results');
  const rawComments = payload.list;
  if (!Array.isArray(rawComments)) {
    throw new ParseError('Copy Manga comments response is missing list', {
      source: COPY_MANGA_SOURCE_ID,
      snippet: text.slice(0, 500),
    });
  }

  const totalItems = numberField(payload.total);
  const totalPages = totalItems !== undefined ? Math.ceil(totalItems / pageSize) : undefined;
  return {
    items: rawComments.map((rawComment, index) =>
      mapCopyMangaWebComment(rawComment, index, opts?.parentId),
    ),
    page,
    hasMore: totalPages !== undefined ? page < totalPages : rawComments.length >= pageSize,
    ...(totalPages !== undefined ? { totalPages } : {}),
    ...(totalItems !== undefined ? { totalItems } : {}),
  };
}

async function loadCopyMangaWebComicUuid(
  id: string,
  proxyFetch: NonNullable<VeneraLoadOptions['proxyFetch']>,
): Promise<string> {
  if (looksLikeUuid(id)) return id;
  return tryCopyMangaWebBases(async (baseUrl) => {
    const html = await fetchCopyMangaText(`${baseUrl}/comic/${encodeURIComponent(id)}`, proxyFetch);
    const uuid = extractCopyMangaWebComicUuid(html);
    if (!uuid) {
      throw new ParseError('Copy Manga web detail did not include comic UUID', {
        source: COPY_MANGA_SOURCE_ID,
        snippet: html.slice(0, 500),
      });
    }
    return uuid;
  }, 'comment comic id');
}

function extractCopyMangaWebComicUuid(html: string): string | undefined {
  return /collect\(['"]([0-9a-f-]{36})['"]\)/i.exec(html)?.[1];
}

function mapCopyMangaWebComment(
  value: unknown,
  index: number,
  fallbackParentId?: string,
): SourceComment {
  const record = asRecord(value, `comment ${index}`);
  const authorName = stringField(record.user_name);
  const body = stringField(record.comment);
  if (!authorName || !body) {
    throw new ParseError('Copy Manga comment is missing author or body', {
      source: COPY_MANGA_SOURCE_ID,
    });
  }

  const comment: SourceComment = {
    id: stringField(record.id) ?? String(index),
    authorName,
    body,
  };
  const authorAvatarUrl = stringField(record.user_avatar);
  if (authorAvatarUrl !== undefined) comment.authorAvatarUrl = authorAvatarUrl;
  const createdAt = timestampField(record.create_at);
  if (createdAt !== undefined) comment.createdAt = createdAt;
  const replyCount = numberField(record.count);
  if (replyCount !== undefined) comment.replyCount = replyCount;
  const parentId = stringField(record.parent_id) ?? fallbackParentId;
  if (parentId !== undefined) comment.parentId = parentId;
  return comment;
}

async function loadCopyMangaWebChaptersWithKey(
  id: string,
  proxyFetch: NonNullable<VeneraLoadOptions['proxyFetch']>,
  baseUrl: string,
  key: string,
): Promise<Record<string, unknown>> {
  const url = `${baseUrl}/comicdetail/${encodeURIComponent(id)}/chapters`;
  let lastError: unknown;
  for (let attempt = 0; attempt < COPY_MANGA_WEB_CHAPTERS_MAX_ATTEMPTS; attempt += 1) {
    try {
      const encrypted = await fetchCopyMangaJsonField(
        attempt === 0 ? url : withCacheBuster(url),
        proxyFetch,
        'results',
      );
      const payload = asRecord(await decryptCopyMangaWebPayload(encrypted, key), 'web chapters');
      copyMangaWebChapterGroups(payload);
      return payload;
    } catch (err) {
      lastError = err;
      if (attempt < COPY_MANGA_WEB_CHAPTERS_MAX_ATTEMPTS - 1) {
        await wait(COPY_MANGA_WEB_CHAPTERS_RETRY_WAIT_MS);
      }
    }
  }
  throw new ParseError('Copy Manga web chapters response did not include readable groups', {
    source: COPY_MANGA_SOURCE_ID,
    cause: lastError,
  });
}

function mapCopyMangaWebReadGroups(
  payload: Record<string, unknown>,
): NonNullable<ComicDetails['readGroups']> {
  const groups = copyMangaWebChapterGroups(payload);
  return Object.values(groups).flatMap((rawGroup): NonNullable<ComicDetails['readGroups']> => {
    const group = asRecord(rawGroup, 'web chapter group');
    const title = stringField(group.name) ?? stringField(group.path_word);
    const groupId = title ?? stringField(group.path_word);
    const chapters = Array.isArray(group.chapters) ? group.chapters : [];
    if (!groupId || !title || chapters.length === 0) return [];
    const units = chapters.flatMap((rawChapter) => {
      const chapter = asRecord(rawChapter, 'web chapter');
      const unitId = stringField(chapter.id);
      const unitTitle = stringField(chapter.name) ?? unitId;
      return unitId && unitTitle ? [{ id: unitId, title: unitTitle }] : [];
    });
    return units.length > 0 ? [{ id: groupId, title, units }] : [];
  });
}

function copyMangaWebChapterGroups(payload: Record<string, unknown>): Record<string, unknown> {
  const directGroups = recordField(payload.groups);
  const buildGroups = recordField(recordField(payload.build)?.groups);
  if (directGroups && hasReadableCopyMangaWebGroup(directGroups)) return directGroups;
  if (buildGroups && hasReadableCopyMangaWebGroup(buildGroups)) return buildGroups;
  throw new ParseError('Copy Manga web chapter groups must include readable chapters', {
    source: COPY_MANGA_SOURCE_ID,
  });
}

function hasReadableCopyMangaWebGroup(groups: Record<string, unknown>): boolean {
  return Object.values(groups).some((rawGroup) => {
    const group = recordField(rawGroup);
    return Array.isArray(group?.chapters) && group.chapters.length > 0;
  });
}

async function firstCopyMangaWebReadUnitId(
  id: string,
  proxyFetch: NonNullable<VeneraLoadOptions['proxyFetch']>,
): Promise<string | undefined> {
  return tryCopyMangaWebBases(async (baseUrl) => {
    const detailUrl = `${baseUrl}/comic/${encodeURIComponent(id)}`;
    const { html, key } = await fetchCopyMangaDetailHtml(detailUrl, proxyFetch);
    const document = parseDocument(html);
    const startLinkUnitId = copyMangaWebDetailReadGroups(document, id)[0]?.units[0]?.id;
    if (startLinkUnitId) return startLinkUnitId;

    const payload = await loadCopyMangaWebChaptersWithKey(id, proxyFetch, baseUrl, key);
    return mapCopyMangaWebReadGroups(payload)[0]?.units[0]?.id;
  }, 'chapters');
}

async function loadCopyMangaWebPages(
  id: string,
  readUnitId: string,
  proxyFetch: NonNullable<VeneraLoadOptions['proxyFetch']>,
): Promise<PageRef[]> {
  try {
    return await loadCopyMangaApiPages(id, readUnitId, proxyFetch);
  } catch {
    // The web reader remains useful when the app-shaped API blocks a request.
  }
  return tryCopyMangaWebBases(async (baseUrl) => {
    const url = `${baseUrl}/comic/${encodeURIComponent(id)}/chapter/${encodeURIComponent(readUnitId)}`;
    const html = await fetchCopyMangaReaderHtml(url, proxyFetch);
    const key = extractScriptString(html, 'cct');
    const encrypted = extractScriptString(html, 'contentKey');
    const pages = await decryptCopyMangaWebPayload(encrypted, key);
    if (!Array.isArray(pages)) {
      throw new ParseError('Copy Manga web chapter payload must be an array', {
        source: COPY_MANGA_SOURCE_ID,
        snippet: html.slice(0, 500),
      });
    }
    return pages.map((rawPage, index) => {
      const page = asRecord(rawPage, 'web page');
      const rawUrl = stringField(page.url);
      if (!rawUrl) {
        throw new ParseError('Copy Manga web page is missing image URL', {
          source: COPY_MANGA_SOURCE_ID,
        });
      }
      return {
        comicId: id,
        index,
        readUnitId,
        resolvedUrl: absoluteUrl(rawUrl, url),
      };
    });
  }, 'reader pages');
}

async function loadCopyMangaApiPages(
  id: string,
  readUnitId: string,
  proxyFetch: NonNullable<VeneraLoadOptions['proxyFetch']>,
): Promise<PageRef[]> {
  return tryCopyMangaApiBases(async (baseUrl) => {
    const url = `${baseUrl}/api/v3/comic/${encodeURIComponent(id)}/chapter2/${encodeURIComponent(readUnitId)}?platform=1&_update=true`;
    const response = await proxyFetch(url, {
      headers: {
        Accept: 'application/json',
        'X-Forward-Headers': await copyMangaAppForwardedHeaders(),
      },
    });
    if (!response.ok) {
      throw new NetworkError(`Copy Manga chapter API request failed: ${response.status}`, {
        source: COPY_MANGA_SOURCE_ID,
        status: response.status,
      });
    }

    const text = await response.text();
    let parsed: unknown;
    try {
      parsed = JSON.parse(text) as unknown;
    } catch (err) {
      throw new ParseError('Copy Manga chapter API response is not valid JSON', {
        source: COPY_MANGA_SOURCE_ID,
        snippet: text.slice(0, 500),
        cause: err,
      });
    }

    const root = asRecord(parsed, 'chapter API response');
    const code = numberField(root.code);
    if (code !== undefined && code !== 200) {
      throw new ParseError(`Copy Manga chapter API returned code ${code}`, {
        source: COPY_MANGA_SOURCE_ID,
        snippet: text.slice(0, 500),
      });
    }
    const chapter = asRecord(asRecord(root.results, 'chapter API results').chapter, 'chapter API chapter');
    const contents = chapter.contents;
    if (!Array.isArray(contents)) {
      throw new ParseError('Copy Manga chapter API response is missing contents', {
        source: COPY_MANGA_SOURCE_ID,
        snippet: text.slice(0, 500),
      });
    }

    const orderedPages = orderCopyMangaApiContents(contents, chapter.words);
    return orderedPages.map((rawPage, index) => {
      const page = asRecord(rawPage, 'chapter API page');
      const rawUrl = stringField(page.url);
      if (!rawUrl) {
        throw new ParseError('Copy Manga chapter API page is missing image URL', {
          source: COPY_MANGA_SOURCE_ID,
        });
      }
      return {
        comicId: id,
        index,
        readUnitId,
        resolvedUrl: copyMangaPreferredImageUrl(rawUrl),
      };
    });
  }, 'chapter API pages');
}

function copyMangaPreferredImageUrl(url: string): string {
  return url.replace(
    /([./])c\d+x\.[a-zA-Z]+$/,
    `$1c${COPY_MANGA_DEFAULTS.image_quality}x.webp`,
  );
}

function orderCopyMangaApiContents(contents: unknown[], words: unknown): unknown[] {
  if (!Array.isArray(words) || words.length !== contents.length) return contents;
  const ordered = new Array<unknown>(contents.length);
  for (let index = 0; index < contents.length; index += 1) {
    const order = numberField(words[index]);
    if (order === undefined || order < 0 || order >= contents.length) return contents;
    ordered[order] = contents[index];
  }
  return ordered.every((page) => page !== undefined) ? ordered : contents;
}

async function tryCopyMangaApiBases<T>(load: (baseUrl: string) => Promise<T>, label: string): Promise<T> {
  let lastError: unknown;
  for (const baseUrl of COPY_MANGA_APP_API_BASES) {
    try {
      return await load(baseUrl);
    } catch (err) {
      lastError = err;
    }
  }
  throw new ParseError(`Copy Manga ${label} failed on every known host`, {
    source: COPY_MANGA_SOURCE_ID,
    cause: lastError,
  });
}

async function tryCopyMangaWebBases<T>(
  load: (baseUrl: string) => Promise<T>,
  label: string,
): Promise<T> {
  let lastError: unknown;
  for (const baseUrl of COPY_MANGA_WEB_BASES) {
    try {
      return await load(baseUrl);
    } catch (err) {
      lastError = err;
    }
  }
  throw new ParseError(`Copy Manga web ${label} failed on every known host`, {
    source: COPY_MANGA_SOURCE_ID,
    cause: lastError,
  });
}

async function fetchCopyMangaText(
  url: string,
  proxyFetch: NonNullable<VeneraLoadOptions['proxyFetch']>,
  opts: { cookies?: string; transport?: 'browser' } = {},
): Promise<string> {
  const response = await proxyFetch(url, {
    headers: { Accept: 'text/html,application/json' },
    ...(opts.cookies ? { cookies: opts.cookies } : {}),
    ...(opts.transport ? { transport: opts.transport } : {}),
  });
  if (!response.ok) {
    throw new ParseError(`Copy Manga web request failed: ${response.status}`, {
      source: COPY_MANGA_SOURCE_ID,
    });
  }
  return response.text();
}

async function fetchCopyMangaDetailHtml(
  url: string,
  proxyFetch: NonNullable<VeneraLoadOptions['proxyFetch']>,
): Promise<{ html: string; key: string }> {
  let lastHtml = '';
  for (let attempt = 0; attempt < COPY_MANGA_WEB_DETAIL_MAX_ATTEMPTS; attempt += 1) {
    const nextUrl = attempt === 0 ? url : withCacheBuster(url);
    const html = await fetchCopyMangaText(nextUrl, proxyFetch);
    lastHtml = html;
    const key = readScriptString(html, 'ccz');
    if (key && hasCopyMangaDetailMetadata(html)) {
      return { html, key };
    }
    if (attempt < COPY_MANGA_WEB_DETAIL_MAX_ATTEMPTS - 1) {
      await wait(COPY_MANGA_WEB_DETAIL_RETRY_WAIT_MS);
    }
  }
  throw new ParseError('Copy Manga web detail is missing title, cover, or chapter key', {
    source: COPY_MANGA_SOURCE_ID,
    snippet: lastHtml.slice(0, 500),
  });
}

async function fetchCopyMangaReaderHtml(
  url: string,
  proxyFetch: NonNullable<VeneraLoadOptions['proxyFetch']>,
): Promise<string> {
  let lastHtml = '';
  let browserTransportError: unknown;
  for (let attempt = 0; attempt < COPY_MANGA_WEB_READER_MAX_ATTEMPTS; attempt += 1) {
    const nextUrl = attempt === 0 ? url : withCacheBuster(url);
    const html = await fetchCopyMangaText(nextUrl, proxyFetch, {
      cookies: COPY_MANGA_WEB_IMAGE_COOKIES,
    });
    lastHtml = html;
    const contentKey = readScriptString(html, 'contentKey');
    if (contentKey) return html;

    if (attempt === 0) {
      try {
        const browserHtml = await fetchCopyMangaText(url, proxyFetch, {
          transport: 'browser',
        });
        lastHtml = browserHtml;
        if (readScriptString(browserHtml, 'contentKey')) return browserHtml;
      } catch (err) {
        browserTransportError = err;
      }
    }

    if (attempt < COPY_MANGA_WEB_READER_MAX_ATTEMPTS - 1) {
      await wait(COPY_MANGA_WEB_READER_RETRY_WAIT_MS);
    }
  }
  throw new ParseError('Copy Manga web reader page did not include an image payload', {
    source: COPY_MANGA_SOURCE_ID,
    snippet: lastHtml.slice(0, 500),
    cause: browserTransportError,
  });
}

async function fetchCopyMangaJsonField(
  url: string,
  proxyFetch: NonNullable<VeneraLoadOptions['proxyFetch']>,
  field: string,
): Promise<string> {
  const text = await fetchCopyMangaText(url, proxyFetch);
  const json = JSON.parse(text) as Record<string, unknown>;
  const value = json[field];
  if (typeof value !== 'string') {
    throw new ParseError(`Copy Manga web response is missing ${field}`, {
      source: COPY_MANGA_SOURCE_ID,
      snippet: text.slice(0, 500),
    });
  }
  return value;
}

function labeledDetailText(document: Document, label: string): string | undefined {
  for (const item of Array.from(document.querySelectorAll('.comicParticulars-title-right li'))) {
    const itemLabel = textOf(item.querySelector('span:first-child')).replace(/：$/, '');
    if (itemLabel === label) {
      const value =
        textOf(item.querySelector('.comicParticulars-right-txt')) ||
        textOf(item.querySelector('p')) ||
        textOf(item).replace(new RegExp(`^${escapeRegExp(label)}：?\\s*`), '');
      return value || undefined;
    }
  }
  return undefined;
}

function extractScriptString(html: string, variableName: string): string {
  const value = readScriptString(html, variableName);
  if (!value) {
    throw new ParseError(`Copy Manga web page is missing ${variableName}`, {
      source: COPY_MANGA_SOURCE_ID,
      snippet: html.slice(0, 500),
    });
  }
  return value;
}

function readScriptString(html: string, variableName: string): string | undefined {
  const match = new RegExp(`var\\s+${escapeRegExp(variableName)}\\s*=\\s*'([^']*)'`).exec(html);
  return match?.[1] || undefined;
}

async function copyMangaAppForwardedHeaders(): Promise<string> {
  const now = new Date(Date.now());
  const timestamp = Math.floor(now.getTime() / 1000).toString();
  const headers = {
    'User-Agent': `COPY/${COPY_MANGA_APP_VERSION}`,
    source: 'copyApp',
    deviceinfo: 'Bunko;Web;1.0;Chrome;1.0',
    dt: `${now.getFullYear()}.${String(now.getMonth() + 1).padStart(2, '0')}.${String(now.getDate()).padStart(2, '0')}`,
    platform: '3',
    referer: `com.copymanga.app-${COPY_MANGA_APP_VERSION}`,
    version: COPY_MANGA_APP_VERSION,
    device: copyMangaRequestId(),
    pseudoid: copyMangaRequestId(),
    Accept: 'application/json',
    region: COPY_MANGA_DEFAULTS.region,
    authorization: 'Token',
    umstring: COPY_MANGA_APP_UMSTRING,
    'x-auth-timestamp': timestamp,
    'x-auth-signature': hmacString('sha256', decodeBase64(COPY_MANGA_APP_HMAC_SECRET), timestamp),
  };
  return btoa(JSON.stringify(headers));
}

function copyMangaRequestId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function hasCopyMangaDetailMetadata(html: string): boolean {
  const document = parseDocument(html);
  const title = textOf(document.querySelector('.comicParticulars-title h6'));
  const rawCoverUrl =
    document.querySelector('.comicParticulars-left-img img')?.getAttribute('data-src') ??
    document.querySelector('.comicParticulars-left-img img')?.getAttribute('src') ??
    '';
  return Boolean(title && rawCoverUrl);
}

function copyMangaWebDetailReadGroups(
  document: Document,
  id: string,
): NonNullable<ComicDetails['readGroups']> {
  const escapedId = escapeRegExp(id);
  const chapterHrefPattern = new RegExp(
    `/comic/${escapedId}/chapter/([0-9a-f-]{36})(?:[/?#]|$)`,
    'i',
  );
  for (const anchor of Array.from(document.querySelectorAll<HTMLAnchorElement>('a[href]'))) {
    const href = anchor.getAttribute('href') ?? '';
    const match = chapterHrefPattern.exec(href);
    const unitId = match?.[1];
    if (!unitId) continue;
    return [
      {
        id: 'default',
        title: 'Default',
        units: [{ id: unitId, title: textOf(anchor) || 'Start reading' }],
      },
    ];
  }
  return [];
}

async function decryptCopyMangaWebPayload(payload: string, key: string): Promise<unknown> {
  const iv = new TextEncoder().encode(payload.slice(0, 16));
  const ciphertext = hexToBytes(payload.slice(16));
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(key),
    { name: 'AES-CBC' },
    false,
    ['decrypt'],
  );
  const plaintext = await crypto.subtle.decrypt({ name: 'AES-CBC', iv }, cryptoKey, ciphertext);
  return JSON.parse(new TextDecoder().decode(plaintext)) as unknown;
}

function hexToBytes(hex: string): Uint8Array<ArrayBuffer> {
  if (hex.length % 2 !== 0) {
    throw new ParseError('Copy Manga encrypted payload has invalid hex length', {
      source: COPY_MANGA_SOURCE_ID,
    });
  }
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

function asRecord(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new ParseError(`Copy Manga ${label} must be an object`, {
      source: COPY_MANGA_SOURCE_ID,
    });
  }
  return value as Record<string, unknown>;
}

function recordField(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function withCacheBuster(url: string): string {
  const parsed = new URL(url);
  parsed.searchParams.set('_bunko_retry', String(Date.now()));
  return parsed.toString();
}

function withCopyMangaThrottleRetry(
  delegate: NonNullable<VeneraLoadOptions['proxyFetch']>,
): NonNullable<VeneraLoadOptions['proxyFetch']> {
  return async (url, opts) => {
    let lastThrottleResponse: { response: Response; body: string } | undefined;
    for (let attempt = 1; attempt <= COPY_MANGA_THROTTLE_MAX_ATTEMPTS; attempt += 1) {
      const response = await delegate(url, opts);
      if (response.status !== COPY_MANGA_THROTTLE_STATUS) return response;

      const body = await response.text();
      lastThrottleResponse = { response, body };
      if (attempt >= COPY_MANGA_THROTTLE_MAX_ATTEMPTS) break;

      const waitMs = parseCopyMangaThrottleWaitMs(body);
      if (waitMs === undefined || waitMs > COPY_MANGA_BRIDGE_SAFE_WAIT_MS) break;
      await wait(waitMs);
    }

    if (lastThrottleResponse) {
      return rebuildResponse(
        lastThrottleResponse.response,
        lastThrottleResponse.body,
        COPY_MANGA_REWRITTEN_THROTTLE_STATUS,
      );
    }
    return delegate(url, opts);
  };
}

function parseCopyMangaThrottleWaitMs(body: string): number | undefined {
  try {
    const parsed = JSON.parse(body) as { message?: unknown };
    const message = typeof parsed.message === 'string' ? parsed.message : '';
    const match = /Expected available in\s+(\d+)\s*seconds?/i.exec(message);
    if (match?.[1]) {
      return Math.max(0, Number.parseInt(match[1], 10) * 1000);
    }
  } catch {
    return undefined;
  }
  return undefined;
}

function rebuildResponse(response: Response, body: string, status = response.status): Response {
  return new Response(body, {
    status,
    statusText: response.statusText,
    headers: response.headers,
  });
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => globalThis.setTimeout(resolve, ms));
}

export { createCopyMangaSource, COPY_MANGA_DEFAULTS };
export default source;
